import React, { useState, useEffect, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import {
  updateSalary,
  getSalaryDetails,
  clearError,
  clearSuccess,
} from "../../../../redux/Slices/authSlice";
import { FaArrowLeft, FaSave } from "react-icons/fa";
import { toast } from "react-toastify";
import { useTranslation } from "react-i18next";
import axios from "axios";
import {
  BASE_URL,
  getToken,
  getCurrentLanguage,
} from "../../../../shared/utils.js/utils";

const EditSalary = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { id } = useParams(); // هذا employee.id
  const [salaryId, setSalaryId] = useState(null); // هذا هو salary.id الصحيح
  const { t, i18n } = useTranslation("global");
  const isRTL = i18n.language === "ar";
  const { salaryDetails, isLoading, error, success } = useSelector(
    state => state.auth,
  );
  const [formData, setFormData] = useState({
    base_salary: "",
    allowances: {
      housing: "",
      transportation: "",
    },
    deductions: {
      social_insurance: "",
    },
    effective_date: "",
  });
  const textAlign = isRTL ? "right" : "left";
  const dir = isRTL ? "rtl" : "ltr";
  const lastErrorRef = useRef({ message: null, time: 0 });
  const [isFetching, setIsFetching] = useState(true);

  useEffect(() => {
    const fetchSalaryData = async () => {
      try {
        const token = getToken();
        const currentLang = getCurrentLanguage();

        if (!token) {
          throw new Error("No authentication token found");
        }

        const response = await axios.get(`${BASE_URL}/employees/${id}/salary`, {
          headers: {
            Authorization: `Bearer ${token}`,
            "Accept-Language": currentLang,
          },
        });

        const salary = response.data?.data || response.data;
        if (salary) {
          setFormData({
            base_salary: salary.base_salary || "",
            allowances: {
              housing: salary.allowances?.housing || "",
              transportation: salary.allowances?.transportation || "",
            },
            deductions: {
              social_insurance: salary.deductions?.social_insurance || "",
            },
            effective_date: salary.effective_date || "",
          });
          // حفظ salary.id في متغير منفصل
          if (salary.id) setSalaryId(salary.id);
        }
      } catch (err) {
        toast.error(
          err.response?.data?.message || t("salaries.edit.errors.fetchFailed"),
          { rtl: isRTL },
        );
      } finally {
        setIsFetching(false);
      }
    };

    if (id) {
      fetchSalaryData();
    }
  }, [id, isRTL, t]);

  useEffect(() => {
    if (success) {
      toast.success(success, { rtl: isRTL });
      dispatch(clearSuccess());
      navigate("/salaries");
    }
    if (error) {
      const now = Date.now();
      const last = lastErrorRef.current;
      if (!last.message || last.message !== error || now - last.time > 2000) {
        toast.error(error, { rtl: isRTL });
        lastErrorRef.current = { message: error, time: now };
      }
      setTimeout(() => dispatch(clearError()), 3000);
    }
  }, [success, error, dispatch, navigate, isRTL]);

  const handleInputChange = e => {
    const { name, value } = e.target;

    if (name.startsWith("allowances.")) {
      const field = name.split(".")[1];
      setFormData(prev => ({
        ...prev,
        allowances: {
          ...prev.allowances,
          [field]: value,
        },
      }));
    } else if (name.startsWith("deductions.")) {
      const field = name.split(".")[1];
      setFormData(prev => ({
        ...prev,
        deductions: {
          ...prev.deductions,
          [field]: value,
        },
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: value,
      }));
    }
  };

  const handleSubmit = e => {
    e.preventDefault();

    if (!formData.base_salary) {
      toast.error(t("salaries.edit.validation.enterBaseSalary"), {
        rtl: isRTL,
      });
      return;
    }

    if (parseFloat(formData.base_salary) < 0) {
      toast.error(t("salaries.edit.validation.baseSalaryNegative"), {
        rtl: isRTL,
      });
      return;
    }

    if (
      formData.allowances.housing &&
      parseFloat(formData.allowances.housing) < 0
    ) {
      toast.error(t("salaries.edit.validation.housingNegative"), {
        rtl: isRTL,
      });
      return;
    }

    if (
      formData.allowances.transportation &&
      parseFloat(formData.allowances.transportation) < 0
    ) {
      toast.error(t("salaries.edit.validation.transportationNegative"), {
        rtl: isRTL,
      });
      return;
    }

    if (
      formData.deductions.social_insurance &&
      parseFloat(formData.deductions.social_insurance) < 0
    ) {
      toast.error(t("salaries.edit.validation.socialInsuranceNegative"), {
        rtl: isRTL,
      });
      return;
    }

    if (!formData.effective_date) {
      toast.error(t("salaries.edit.validation.enterEffectiveDate"), {
        rtl: isRTL,
      });
      return;
    }

    const salaryData = {
      base_salary: parseFloat(formData.base_salary),
      allowances: {
        housing: formData.allowances.housing
          ? parseFloat(formData.allowances.housing)
          : 0,
        transportation: formData.allowances.transportation
          ? parseFloat(formData.allowances.transportation)
          : 0,
      },
      deductions: {
        social_insurance: formData.deductions.social_insurance
          ? parseFloat(formData.deductions.social_insurance)
          : 0,
      },
      effective_date: formData.effective_date,
    };

    // استخدم salaryId وليس id القادم من useParams
    if (!salaryId) {
      toast.error(
        t("salaries.edit.errors.noSalaryId", {
          defaultValue: "لا يوجد معرف للراتب",
        }),
        { rtl: isRTL },
      );
      return;
    }
    dispatch(updateSalary({ id: salaryId, salaryData }));
  };

  if (isFetching) {
    return (
      <div
        style={{
          padding: "30px",
          backgroundColor: "#14263b",
          minHeight: "calc(100vh - 80px)",
          color: "white",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          direction: dir,
        }}
      >
        <div>{t("salaries.edit.loading")}</div>
      </div>
    );
  }

  return (
    <div
      style={{
        padding: "30px",
        backgroundColor: "#14263b",
        minHeight: "calc(100vh - 80px)",
        color: "white",
        direction: dir,
      }}
    >
      <div style={{ marginBottom: "30px" }}>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: "15px",
            marginBottom: "20px",
          }}
        >
          <button
            onClick={() => navigate("/salaries")}
            style={{
              backgroundColor: "#666",
              color: "white",
              border: "none",
              padding: "10px",
              borderRadius: "8px",
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              gap: "8px",
              flexDirection: isRTL ? "row" : "row-reverse",
            }}
          >
            <FaArrowLeft />
            {t("salaries.edit.back")}
          </button>
          <h1
            style={{
              fontSize: "24px",
              fontWeight: "bold",
              color: "white",
              margin: 0,
              textAlign,
            }}
          >
            {t("salaries.edit.title")}
          </h1>
        </div>
      </div>

      <div
        style={{
          backgroundColor: "#1b3350",
          borderRadius: "12px",
          padding: "30px",
          border: "1px solid #333",
        }}
      >
        <form onSubmit={handleSubmit}>
          <div style={{ display: "flex", gap: "30px", flexWrap: "wrap" }}>
            {/* العمود الأيسر */}
            <div style={{ flex: "1", minWidth: "300px" }}>
              <div style={{ marginBottom: "20px" }}>
                <label
                  style={{
                    display: "block",
                    marginBottom: "8px",
                    color: "white",
                    fontWeight: "500",
                    textAlign,
                  }}
                >
                  {t("salaries.edit.fields.baseSalary")}
                </label>
                <input
                  type="number"
                  name="base_salary"
                  value={formData.base_salary}
                  onChange={handleInputChange}
                  min="0"
                  placeholder={t("salaries.edit.placeholders.baseSalary")}
                  style={{
                    width: "100%",
                    padding: "12px 15px",
                    backgroundColor: "#1a1f2e",
                    border: "1px solid #333",
                    borderRadius: "8px",
                    color: "white",
                    fontSize: "14px",
                    textAlign,
                    direction: dir,
                  }}
                />
              </div>

              <div style={{ marginBottom: "20px" }}>
                <label
                  style={{
                    display: "block",
                    marginBottom: "8px",
                    color: "white",
                    fontWeight: "500",
                    textAlign,
                  }}
                >
                  {t("salaries.edit.fields.effectiveDate")}
                </label>
                <input
                  type="date"
                  name="effective_date"
                  value={formData.effective_date}
                  onChange={handleInputChange}
                  style={{
                    width: "100%",
                    padding: "12px 15px",
                    backgroundColor: "#1a1f2e",
                    border: "1px solid #333",
                    borderRadius: "8px",
                    color: "white",
                    fontSize: "14px",
                    textAlign,
                    direction: dir,
                  }}
                />
              </div>
            </div>

            {/* العمود الأيمن */}
            <div style={{ flex: "1", minWidth: "300px" }}>
              {/* البدلات */}
              <div style={{ marginBottom: "20px" }}>
                <label
                  style={{
                    display: "block",
                    marginBottom: "8px",
                    color: "white",
                    fontWeight: "500",
                    textAlign,
                  }}
                >
                  {t("salaries.edit.fields.allowances")}
                </label>

                <div style={{ marginBottom: "15px" }}>
                  <label
                    style={{
                      display: "block",
                      marginBottom: "5px",
                      color: "#ccc",
                      fontSize: "12px",
                      textAlign,
                    }}
                  >
                    {t("salaries.edit.labels.housing")}
                  </label>
                  <input
                    type="number"
                    name="allowances.housing"
                    value={formData.allowances.housing}
                    onChange={handleInputChange}
                    min="0"
                    placeholder={t("salaries.edit.placeholders.housing")}
                    style={{
                      width: "100%",
                      padding: "10px 12px",
                      backgroundColor: "#1a1f2e",
                      border: "1px solid #333",
                      borderRadius: "6px",
                      color: "white",
                      fontSize: "14px",
                      textAlign,
                      direction: dir,
                    }}
                  />
                </div>

                <div>
                  <label
                    style={{
                      display: "block",
                      marginBottom: "5px",
                      color: "#ccc",
                      fontSize: "12px",
                      textAlign,
                    }}
                  >
                    {t("salaries.edit.labels.transportation")}
                  </label>
                  <input
                    type="number"
                    name="allowances.transportation"
                    value={formData.allowances.transportation}
                    onChange={handleInputChange}
                    min="0"
                    placeholder={t("salaries.edit.placeholders.transportation")}
                    style={{
                      width: "100%",
                      padding: "10px 12px",
                      backgroundColor: "#1a1f2e",
                      border: "1px solid #333",
                      borderRadius: "6px",
                      color: "white",
                      fontSize: "14px",
                      textAlign,
                      direction: dir,
                    }}
                  />
                </div>
              </div>
            </div>
          </div>

          {/* الخصومات - col-12 */}
          <div style={{ marginBottom: "20px", width: "100%" }}>
            <label
              style={{
                display: "block",
                marginBottom: "8px",
                color: "white",
                fontWeight: "500",
                textAlign,
              }}
            >
              {t("salaries.edit.fields.deductions")}
            </label>

            <div>
              <label
                style={{
                  display: "block",
                  marginBottom: "5px",
                  color: "#ccc",
                  fontSize: "12px",
                  textAlign,
                }}
              >
                {t("salaries.edit.labels.socialInsurance")}
              </label>
              <input
                type="number"
                name="deductions.social_insurance"
                value={formData.deductions.social_insurance}
                onChange={handleInputChange}
                min="0"
                placeholder={t("salaries.edit.placeholders.socialInsurance")}
                style={{
                  width: "100%",
                  padding: "12px 15px",
                  backgroundColor: "#1a1f2e",
                  border: "1px solid #333",
                  borderRadius: "8px",
                  color: "white",
                  fontSize: "14px",
                  textAlign,
                  direction: dir,
                }}
              />
            </div>
          </div>

          {/* أزرار الإجراء */}
          <div
            style={{
              display: "flex",
              gap: "15px",
              marginTop: "30px",
              justifyContent: "center",
            }}
          >
            <button
              type="submit"
              disabled={isLoading}
              style={{
                backgroundColor: isLoading ? "#666" : "#AC2000",
                color: "white",
                border: "none",
                padding: "12px 30px",
                borderRadius: "8px",
                fontSize: "16px",
                fontWeight: "bold",
                cursor: isLoading ? "not-allowed" : "pointer",
                display: "flex",
                alignItems: "center",
                gap: "8px",
                opacity: isLoading ? 0.6 : 1,
              }}
            >
              <FaSave />
              {isLoading ? t("salaries.edit.saving") : t("salaries.edit.save")}
            </button>

            <button
              type="button"
              onClick={() => navigate("/salaries")}
              style={{
                backgroundColor: "#666",
                color: "white",
                border: "none",
                padding: "12px 30px",
                borderRadius: "8px",
                fontSize: "16px",
                fontWeight: "bold",
                cursor: "pointer",
              }}
            >
              {t("salaries.edit.cancel")}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default EditSalary;
